import { type FormEvent, useState } from "react";
import { Box, Button, styled, type Theme } from "~/common/mui";
import SearchInput from "~/features/search/components/Autosuggest/SearchInput";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useFetchData } from "~/features/search/components/Autosuggest/SearchBox";
import CenterContents from "~/common/components/CenterContents";
import NullableImage from "~/common/components/NullableImage";
import ProductPreferredIndicator from "~/features/productDetails/components/ProductPreferredIndicator";
import { getDesignationAbbreviation } from "~/domains/product/utils/designationAbbreviation";
import { Translation } from "~/app/i18n/Translation";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { MEDIA_QUERY_MAX_PHONE } from "~/common/utils/screenSizes";
import { SEARCH_PAGE } from "~/utils/url/staticUrls";
import { useTranslation } from "react-i18next";
import { redirectToUrl } from "~/utils/url/urlUtils";

const sizeInputWidth = (theme: Theme) => ({
  width: "296px",
  [theme.breakpoints.up("tablet")]: { width: "472px" },
  [theme.breakpoints.up("desktop-s")]: { width: "636px" },
  [theme.breakpoints.up("desktop-m")]: { width: "480px" },
  [theme.breakpoints.up("desktop-l")]: { width: "576px" },
});

const CadContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "224px",
  [theme.breakpoints.up("tablet")]: { height: "360px" },
  [theme.breakpoints.up("desktop-l")]: { height: "444px" },
}));

const CadBackgroundImage = styled("div")({
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  background:
    "linear-gradient(0deg, rgba(100, 100, 100, 0.7), rgba(100, 100, 100, 0.7)), url(https://cdn.schaeffler-ecommerce.com/frontend/cad_1792x444.jpg)",
  backgroundSize: "cover",
});

const CadContent = styled("div")({
  zIndex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
});

const CadTitle = styled("div")(({ theme }) => ({
  ...theme.typography.headlineSmall,
  color: theme.palette.common.white,
  marginBottom: theme.spacing(1),
}));

const CadDescription = styled("div")(({ theme }) => ({
  ...theme.typography.labelLarge,
  color: theme.palette.common.white,
  marginBottom: theme.spacing(1),
  textAlign: "center",
  padding: `0 ${theme.spacing(1)}`,
}));

const CadInputWrapper = styled("div")(({ theme }) => ({
  ...sizeInputWidth(theme),
  position: "relative",
}));

const CadResults = styled("div")(({ theme }) => ({
  ...sizeInputWidth(theme),
  display: "block",
  position: "absolute",
  background: theme.palette.common.white,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "0px 0px 4px 4px",
  padding: theme.spacing(2, 4, 4),
  zIndex: 1,
  [theme.breakpoints.up("phone")]: {
    padding: theme.spacing(2, 3, 3),
  },
}));

const CadResultItem = styled("a")(({ theme }) => ({
  padding: theme.spacing(2, 0),
  display: "flex",
  flexDirection: "row",
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:first-of-type": {
    paddingTop: 0,
  },
  [theme.breakpoints.up("phone")]: {
    padding: theme.spacing(1, 0),
  },
}));

const CadResultItemImage = styled(Box)(({ theme }) => ({
  height: "64px",
  width: "64px",
  marginRight: theme.spacing(2),
}));

const CadResultItemContent = styled("div")({
  display: "flex",
  flexDirection: "column",
});

const CadResultItemDesignation = styled("span")(({ theme }) => ({
  ...theme.typography.labelLarge,
  color: theme.palette.text.primary,
  fontWeight: "bold",
}));

const CadButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(4),
  [theme.breakpoints.up("phone")]: {
    marginTop: theme.spacing(2),
  },
}));

export function SchaefflerCADFileSearchComponent() {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const { val: suggestionsVisible, setFalse: hideSuggestions, setTrue: showSuggestions } = useBooleanState(false);

  const data = useFetchData(text, true);
  const isMaxMobile = useMediaQuery(MEDIA_QUERY_MAX_PHONE);
  const products = isMaxMobile ? data.data?.products.slice(0, 4) ?? [] : data.data?.products ?? [];
  const hasMoreResults = !!(data.data && data.data.totalResults && data.data.totalResults > data.data.products.length);

  const searchUrlParams = new URLSearchParams({
    text: text.trim(),
    sort: "relevance",
    initialTabTabular: "true",
  });
  const searchUrl = `${SEARCH_PAGE}?${searchUrlParams.toString()}`;
  let submitUrl: string;
  if (products.length === 1) {
    submitUrl = products[0].cadUrl ?? searchUrl;
  } else {
    submitUrl = searchUrl;
  }

  const clearInput = () => setText("");

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    hideSuggestions();
    redirectToUrl(submitUrl);
  };

  return (
    <CadContainer>
      <CadBackgroundImage />
      <CadContent>
        <CadTitle>{t("cadTaskForce.title")}</CadTitle>
        <CadDescription>{t("cadTaskForce.description")}</CadDescription>
        <CadInputWrapper>
          <form onSubmit={onSubmit}>
            <SearchInput
              variant={"cad"}
              value={text}
              onChange={setText}
              onFocus={showSuggestions}
              onClear={clearInput}
              placeholder={t("cadTaskForce.placeholder")}
            />
          </form>

          {data.state === "loaded" && products.length > 0 && suggestionsVisible && (
            <CadResults>
              {products.map((product, index) => (
                <CadResultItem href={product.cadUrl} key={index}>
                  <CadResultItemImage>
                    <CenterContents>
                      <NullableImage data={product.primaryImage} />
                    </CenterContents>
                  </CadResultItemImage>
                  <CadResultItemContent>
                    <div className={"d-flex"}>
                      {product.preferred && <ProductPreferredIndicator className={"icon-margin"} />}
                      <CadResultItemDesignation>
                        {getDesignationAbbreviation(product.preferred ? 16 : 20)(product.designation)}
                      </CadResultItemDesignation>
                    </div>
                    <p className="my-1 f-carbon">
                      <Translation {...product.shortDescription} />
                    </p>
                  </CadResultItemContent>
                </CadResultItem>
              ))}
              {hasMoreResults && (
                <CadButton fullWidth={true} href={searchUrl}>
                  {t("cadTaskForce.button.text")}
                </CadButton>
              )}
            </CadResults>
          )}
        </CadInputWrapper>
      </CadContent>
    </CadContainer>
  );
}

export function createSchaefflerCADFileSearchComponent() {
  return <SchaefflerCADFileSearchComponent />;
}
